import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { AlertTriangle, Smartphone, Globe, Snowflake, ShieldAlert, Flag } from "lucide-react";
import { toast } from "sonner";
import { RiskBadge } from "@/components/RiskBadge";
import { TierBadge, type Tier } from "@/components/TierBadge";

interface Cluster {
  cluster_type: string;
  cluster_value: string;
  account_count: number;
  user_ids: string[];
  last_seen: string;
}

interface SecurityEvent {
  id: string;
  user_id: string;
  event_type: string;
  details: Record<string, unknown> | null;
  device_id: string | null;
  created_at: string;
}

interface RiskRow {
  user_id: string;
  fraud_score: number;
  trust_score: number;
  dispute_rate: number;
  completion_rate: number;
  txn_velocity_24h: number;
  device_cluster_size: number;
  last_computed_at: string;
  profile?: { display_name: string; cashtag: string; tier: Tier } | null;
}

interface FraudFlag {
  id: string;
  user_id: string;
  kind: string;
  severity: number;
  details: Record<string, unknown> | null;
  resolved: boolean;
  created_at: string;
}

const AdminFraud = () => {
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [events, setEvents] = useState<SecurityEvent[]>([]);
  const [risks, setRisks] = useState<RiskRow[]>([]);
  const [flags, setFlags] = useState<FraudFlag[]>([]);
  const [loading, setLoading] = useState(true);
  const [freezing, setFreezing] = useState<string | null>(null);

  const load = async () => {
    setLoading(true);
    const [clusterRes, eventsRes, riskRes, flagsRes] = await Promise.all([
      supabase.rpc("admin_fraud_clusters", { _limit: 50 }),
      supabase
        .from("security_events")
        .select("*")
        .order("created_at", { ascending: false })
        .limit(100),
      supabase
        .from("risk_scores")
        .select("*")
        .order("fraud_score", { ascending: false })
        .limit(50),
      supabase
        .from("fraud_flags")
        .select("*")
        .eq("resolved", false)
        .order("created_at", { ascending: false })
        .limit(50),
    ]);
    if (clusterRes.error) toast.error(clusterRes.error.message);
    if (eventsRes.error) toast.error(eventsRes.error.message);
    if (riskRes.error) toast.error(riskRes.error.message);
    if (flagsRes.error) toast.error(flagsRes.error.message);

    const riskRows = (riskRes.data ?? []) as RiskRow[];
    if (riskRows.length) {
      const ids = riskRows.map((r) => r.user_id);
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, display_name, cashtag, tier")
        .in("user_id", ids);
      const map = new Map((profs ?? []).map((p: any) => [p.user_id, p]));
      riskRows.forEach((r) => {
        r.profile = (map.get(r.user_id) as any) ?? null;
      });
    }

    setClusters((clusterRes.data ?? []) as Cluster[]);
    setEvents((eventsRes.data ?? []) as SecurityEvent[]);
    setRisks(riskRows);
    setFlags((flagsRes.data ?? []) as FraudFlag[]);
    setLoading(false);
  };

  useEffect(() => {
    load();
  }, []);

  const freezeUser = async (userId: string) => {
    setFreezing(userId);
    const { error } = await supabase.from("wallet_freezes").insert({
      user_id: userId,
      frozen_by: (await supabase.auth.getUser()).data.user?.id,
      reason: "Admin freeze from fraud console",
    });
    setFreezing(null);
    if (error) return toast.error(error.message);
    toast.success("Wallet frozen");
  };

  return (
    <div className="p-4 sm:p-6 max-w-6xl mx-auto space-y-6">
      <div>
        <h1 className="text-xl font-semibold flex items-center gap-2">
          <AlertTriangle className="h-5 w-5 text-destructive" />
          Fraud & risk
        </h1>
        <p className="text-sm text-muted-foreground">
          Devices and IPs shared by multiple accounts, plus the latest security events.
        </p>
      </div>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <ShieldAlert className="h-4 w-4" />
          Highest-risk users
        </h2>
        <Card className="overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-muted/50 text-xs uppercase tracking-wider text-muted-foreground">
                <tr>
                  <th className="text-left px-3 py-2">User</th>
                  <th className="text-left px-3 py-2">Risk</th>
                  <th className="text-right px-3 py-2">Fraud</th>
                  <th className="text-right px-3 py-2">Trust</th>
                  <th className="text-right px-3 py-2">Disp%</th>
                  <th className="text-right px-3 py-2">Vel/24h</th>
                  <th className="text-right px-3 py-2">Devices</th>
                  <th className="text-right px-3 py-2">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {loading && (
                  <tr><td colSpan={8} className="px-3 py-4 text-muted-foreground">Loading…</td></tr>
                )}
                {!loading && risks.length === 0 && (
                  <tr><td colSpan={8} className="px-3 py-4 text-muted-foreground">No risk scores yet.</td></tr>
                )}
                {risks.map((r) => (
                  <tr key={r.user_id} className="hover:bg-muted/30">
                    <td className="px-3 py-2">
                      <div className="flex items-center gap-2 min-w-0">
                        <div className="min-w-0">
                          <p className="font-medium truncate">{r.profile?.display_name ?? "Unknown"}</p>
                          <p className="text-[10px] text-muted-foreground font-mono truncate">${r.profile?.cashtag ?? r.user_id.slice(0, 8)}</p>
                        </div>
                        {r.profile?.tier && <TierBadge tier={r.profile.tier} compact />}
                      </div>
                    </td>
                    <td className="px-3 py-2"><RiskBadge risk={r.fraud_score >= 70 ? "high" : r.fraud_score >= 40 ? "medium" : "low"} /></td>
                    <td className="px-3 py-2 text-right font-mono">{r.fraud_score}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.trust_score}</td>
                    <td className="px-3 py-2 text-right font-mono">{(Number(r.dispute_rate) * 100).toFixed(0)}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.txn_velocity_24h}</td>
                    <td className="px-3 py-2 text-right font-mono">{r.device_cluster_size}</td>
                    <td className="px-3 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        disabled={freezing === r.user_id}
                        onClick={() => freezeUser(r.user_id)}
                      >
                        <Snowflake className="h-3 w-3 mr-1" />
                        Freeze
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground flex items-center gap-2">
          <Flag className="h-4 w-4" />
          Open fraud flags
        </h2>
        <Card className="divide-y divide-border">
          {!loading && flags.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">No open flags.</div>
          )}
          {flags.map((f) => (
            <div key={f.id} className="px-3 py-2 text-sm flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={f.severity >= 80 ? "destructive" : "secondary"} className="text-xs">
                    {f.kind}
                  </Badge>
                  <span className="text-[10px] text-muted-foreground">sev {f.severity}</span>
                  <span className="font-mono text-[10px] text-muted-foreground truncate">
                    {f.user_id}
                  </span>
                </div>
                {f.details && (
                  <pre className="text-[10px] text-muted-foreground mt-1 whitespace-pre-wrap break-all">
                    {JSON.stringify(f.details)}
                  </pre>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground shrink-0">
                {new Date(f.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </Card>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Multi-account clusters
        </h2>
        <div className="grid sm:grid-cols-2 gap-3">
          {loading && <Card className="p-4 text-sm text-muted-foreground">Loading…</Card>}
          {!loading && clusters.length === 0 && (
            <Card className="p-4 text-sm text-muted-foreground">No clusters detected.</Card>
          )}
          {clusters.map((c, i) => (
            <Card key={i} className="p-3 space-y-2">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2 text-sm font-medium">
                  {c.cluster_type === "device" ? (
                    <Smartphone className="h-4 w-4" />
                  ) : (
                    <Globe className="h-4 w-4" />
                  )}
                  <span className="font-mono text-xs truncate max-w-[180px]">
                    {c.cluster_value}
                  </span>
                </div>
                <Badge variant={c.account_count >= 4 ? "destructive" : "secondary"}>
                  {c.account_count} accounts
                </Badge>
              </div>
              <div className="text-[10px] text-muted-foreground font-mono break-all">
                {c.user_ids.slice(0, 3).join(", ")}
                {c.user_ids.length > 3 ? ` +${c.user_ids.length - 3}` : ""}
              </div>
              <div className="text-[10px] text-muted-foreground">
                Last seen {new Date(c.last_seen).toLocaleString()}
              </div>
            </Card>
          ))}
        </div>
      </section>

      <section className="space-y-2">
        <h2 className="text-sm font-medium uppercase tracking-wider text-muted-foreground">
          Recent security events
        </h2>
        <Card className="divide-y divide-border">
          {events.length === 0 && (
            <div className="p-4 text-sm text-muted-foreground">No events.</div>
          )}
          {events.map((e) => (
            <div key={e.id} className="px-3 py-2 text-sm flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="flex items-center gap-2">
                  <Badge variant="outline" className="text-xs">{e.event_type}</Badge>
                  <span className="font-mono text-[10px] text-muted-foreground truncate">
                    {e.user_id}
                  </span>
                </div>
                {e.details && (
                  <pre className="text-[10px] text-muted-foreground mt-1 whitespace-pre-wrap break-all">
                    {JSON.stringify(e.details)}
                  </pre>
                )}
              </div>
              <div className="text-[10px] text-muted-foreground shrink-0">
                {new Date(e.created_at).toLocaleString()}
              </div>
            </div>
          ))}
        </Card>
      </section>
    </div>
  );
};

export default AdminFraud;
